import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Globe,
  Layers,
  Mail,
  Search,
  ShieldCheck,
  Target,
  Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import AuthModal from "../components/auth/AuthModal";
import Footer from "../components/layout/Footer";
import Header from "../components/layout/Header";

import aboutus from "../assets/images/background.jpg";
import { useNavigate } from "react-router-dom";
import Meta from "../components/Meta/Meta";

export default function AboutPage({ isAuthenticated }) {
  const [openWaitingListModal, setOpenWaitingListModal] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState("signup");
  const navigate = useNavigate();

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0.2]);

  const openAuthModal = (tab) => {
    setAuthModalTab(tab);
    setAuthModalOpen(true);
  };

  const proofRef = useRef(null);
  const productRef = useRef(null);
  const flowRef = useRef(null);
  const trustRef = useRef(null);
  const ctaRef = useRef(null);

  const proofInView = useInView(proofRef, { once: true, margin: "-80px" });
  const productInView = useInView(productRef, { once: true, margin: "-80px" });
  const flowInView = useInView(flowRef, { once: true, margin: "-80px" });
  const trustInView = useInView(trustRef, { once: true, margin: "-80px" });
  const ctaInView = useInView(ctaRef, { once: true, margin: "-80px" });

  const container = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12 } },
  };

  const item = {
    hidden: { y: 18, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 120, damping: 16 },
    },
  };

  const stats = [
    { value: "NSE Listed", label: "Digikore Studios Limited" },
    { value: "150+", label: "Creative & Tech Specialists" },
    { value: "AI-led", label: "Automation-Driven Workflows" },
    { value: "Global", label: "Content Delivery Scale" },
  ];

  const productPillars = [
    {
      icon: <Search className="h-6 w-6 text-indigo-600" />,
      title: "Find the right decision-makers",
      desc: "Search companies, roles, and seniority. Get targeted contacts that match your ideal customer profile.",
    },
    {
      icon: <Mail className="h-6 w-6 text-indigo-600" />,
      title: "Launch campaigns in a few clicks",
      desc: "Search Leads, Connect your email account, generate AI based email template and launch. No messy tools or long setup.",
    },
    {
      icon: <Target className="h-6 w-6 text-indigo-600" />,
      title: "Smart targeting & deliverability",
      desc: "Quality filters, clean lists, and outreach best practices baked in-so your emails land where they should.",
    },
    {
      icon: <BarChart3 className="h-6 w-6 text-indigo-600" />,
      title: "Track what works, scale what wins",
      desc: "See opens, replies, and performance signals. Improve messaging, timing, and targeting with clarity.",
    },
  ];

  const steps = [
    {
      step: "01",
      title: "Define your target",
      desc: "Pick industry, company size, titles, and seniority to match your ideal buyers.",
      icon: <Layers className="h-6 w-6 text-indigo-600" />,
    },
    {
      step: "02",
      title: "Get verified contacts",
      desc: "Access decision-maker emails with confidence and keep your lists clean as you scale.",
      icon: <ShieldCheck className="h-6 w-6 text-indigo-600" />,
    },
    {
      step: "03",
      title: "Launch your sequence",
      desc: "Personalize with speed, schedule follow-ups, and run campaigns without friction.",
      icon: <Zap className="h-6 w-6 text-indigo-600" />,
    },
    {
      step: "04",
      title: "Measure & improve",
      desc: "Spot winning angles, refine messaging, and repeat the playbook that converts.",
      icon: <BarChart3 className="h-6 w-6 text-indigo-600" />,
    },
  ];

  const trustPoints = [
    "Built with a quality-first approach to data & outreach",
    "Designed for fast execution - search, select, send",
    "Simple plans, clean UI, and campaign clarity",
    "Made to scale from your first outreach to repeatable growth",
  ];

  return (
    <div className="min-h-screen bg-white text-black overflow-x-hidden">
      <Meta
        title="About MaxiPitch | AI Sales Automation Platform for Businesses"
        description="MaxiPitch, an AI-powered sales automation platform helping businesses generate leads, automate outreach, and grow faster with smart outbound marketing technology."
        url="https://maxipitch.com/about-us"
      />
      <Header />

      {/* HERO */}
      <section ref={heroRef} className="relative pt-0 pb-12 overflow-hidden">
        {/* Background image + light indigo overlay */}
        <motion.div className="absolute inset-0 z-0" style={{ y, opacity }}>
          <div
            className="w-full h-full bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${aboutus})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-indigo-50/75 to-blue-50/70" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(99,102,241,0.20),transparent_55%)]" />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-4">
          <div className="pt-8 sm:pt-20 md:pt-24 pb-10 sm:pb-14">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl"
            >
              <h1 className="mt-6 ">
                Digikore Studios Limited Is Listed On
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 ml-2">
                  The NSE Of India
                </span>
              </h1>

              <p className="mt-5 text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed max-w-2xl font-medium">
                We’re an NSE-listed company with 150+ specialists, creating
                global content at scale. Our AI division builds in-house
                automation that streamlines workflows - and now powers a product
                that helps you reach decision-makers and launch outreach in just
                a few clicks.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  size="lg"
                  onClick={() => {
                    isAuthenticated ? navigate("/search") : navigate("/");
                  }}
                  className="main-button text-white px-7 sm:px-9 py-5 sm:py-6 text-base sm:text-lg rounded-md shadow-lg max-md:w-1/2"
                >
                  Get Started
                </Button>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-gray-700 text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-indigo-600" />
                  Clean lists & targeting
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-indigo-600" />
                  Fast campaign setup
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-indigo-600" />
                  Analytics & iteration
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PROOF / STATS */}
      <section ref={proofRef} className="relative -mt-10 sm:-mt-14 pb-12">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            variants={container}
            initial="hidden"
            animate={proofInView ? "visible" : "hidden"}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {stats.map((s, i) => (
              <motion.div key={i} variants={item}>
                <Card className="border-2 border-indigo-300 shadow-md bg-white rounded-2xl">
                  <CardContent className="p-6">
                    <div className="text-2xl sm:text-3xl font-bold text-gray-900">
                      {s.value}
                    </div>
                    <div className="mt-2 text-sm text-gray-600">{s.label}</div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PRODUCT PILLARS */}
      <section ref={productRef} className="py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="">
              A Simple Way To{""}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 ml-2">
                Reach Decision-Makers
              </span>
            </h2>
            <p className="mt-4 text-sm sm:text-base md:text-lg text-gray-600">
              No juggling tools. No complicated setup. Just search, select,
              launch, and learn - built for momentum.
            </p>
          </motion.div>

          <motion.div
            variants={container}
            initial="hidden"
            animate={productInView ? "visible" : "hidden"}
            className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6"
          >
            {productPillars.map((f, idx) => (
              <motion.div key={idx} variants={item}>
                <Card className="border border-indigo-50 shadow-md rounded-2xl overflow-hidden hover:shadow-lg transition">
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-indigo-50">
                        {f.icon}
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                          {f.title}
                        </h3>
                        <p className="mt-2 text-sm sm:text-base text-gray-600 leading-relaxed">
                          {f.desc}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FLOW SECTION (LIGHT) */}
      <section
        ref={flowRef}
        className="relative py-12 sm:py-16 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-50 via-white to-blue-50" />
        <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.18),transparent_55%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="">
              How You Go From{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 ml-2">
                Search To Launch
              </span>
            </h2>
            <p className="mt-4 text-sm sm:text-base md:text-lg text-gray-600">
              Four steps. Clean flow. Your outreach becomes repeatable.
            </p>
          </motion.div>

          <motion.div
            variants={container}
            initial="hidden"
            animate={flowInView ? "visible" : "hidden"}
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
          >
            {steps.map((s, i) => (
              <motion.div key={i} variants={item}>
                <div className="h-full rounded-2xl border border-indigo-100 bg-white/80 backdrop-blur-md p-6 sm:p-7 hover:bg-white transition shadow-sm hover:shadow-md">
                  <div className="flex items-center justify-between">
                    <div className="text-4xl font-bold text-indigo-100">
                      {s.step}
                    </div>
                    <div className="p-2 rounded-xl bg-indigo-50">{s.icon}</div>
                  </div>
                  <h3 className="mt-4 text-lg sm:text-xl font-bold text-gray-900">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm sm:text-base text-gray-600 leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* TRUST / DIFFERENTIATORS */}
      <section ref={trustRef} className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            variants={container}
            initial="hidden"
            animate={trustInView ? "visible" : "hidden"}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center"
          >
            <motion.div variants={item}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-50">
                <Globe className="h-4 w-4 text-indigo-600" />
                <span className="text-xs sm:text-sm font-medium text-gray-700">
                  Built With Scale In Mind
                </span>
              </div>

              <h2 className="mt-5 ">
                Designed To Feel Effortless
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 ml-2">
                  - And Still Powerful
                </span>
              </h2>

              <p className="mt-4 text-sm sm:text-base text-gray-600 leading-relaxed">
                Our background in automation and high-volume production shaped a
                product that keeps things clean: faster targeting, smoother
                campaign creation, and clearer insights - without the clutter.
              </p>

              <div className="mt-6 space-y-3">
                {trustPoints.map((t, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 text-indigo-600" />
                    <span className="text-sm sm:text-base text-gray-700">
                      {t}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div variants={item}>
              <Card className="border border-indigo-100 shadow-lg rounded-2xl overflow-hidden">
                <CardContent className="p-0">
                  <div className="p-6 sm:p-8 bg-gradient-to-br from-indigo-600 via-blue-600 to-indigo-700 text-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="h-5 w-5" />
                        <span className="text-sm font-medium text-white/95">
                          Outreach readiness checklist
                        </span>
                      </div>
                      <span className="text-xs text-white/75">quick setup</span>
                    </div>

                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        "Target defined",
                        "Contacts selected",
                        "Template ready",
                        "Follow-ups scheduled",
                        "Tracking enabled",
                        "Launch confirmed",
                      ].map((x, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 rounded-lg bg-white/15 px-3 py-2  "
                        >
                          <CheckCircle2 className="h-4 w-4 " />
                          <span className="text-sm text-wrap ">{x}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA (LIGHT) */}
      <section ref={ctaRef} className="relative py-12 sm:py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-50 via-white to-blue-50" />
        <div className="absolute inset-0 opacity-35 bg-[radial-gradient(circle_at_top,rgba(99,102,241,0.20),transparent_60%)]" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          <motion.div
            variants={container}
            initial="hidden"
            animate={ctaInView ? "visible" : "hidden"}
          >
            <motion.h2
              variants={item}
              className="text-4xl max-md:text-3xl font-bold  text-gray-900"
            >
              Ready To Launch Your
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 ml-2">
                Next Campaign In Minutes?
              </span>
            </motion.h2>
            <motion.p
              variants={item}
              className="mt-4 text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto"
            >
              Manage your projects, track progress, and unlock powerful
              features.
            </motion.p>

            <motion.div
              variants={item}
              className="mt-8 flex flex-col sm:flex-row justify-center gap-3 sm:gap-4"
            >
              {isAuthenticated ? (
                <Button
                  size="lg"
                  onClick={() => navigate("/search")}
                  className="main-button text-white  text-base sm:text-lg  shadow-lg"
                >
                  Get Started
                </Button>
              ) : (
                <Button
                  size="lg"
                  onClick={() => openAuthModal("signup")}
                  className="main-button text-white  text-base sm:text-lg  shadow-lg"
                >
                  Sign up
                </Button>
              )}
            </motion.div>

            <motion.div
              variants={item}
              className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-gray-600 text-xs sm:text-sm"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-indigo-600" />
                Better list quality
              </div>
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-indigo-600" />
                Faster launches
              </div>
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-indigo-600" />
                Cleaner targeting
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        defaultTab={authModalTab}
      />
      
    </div>
  );
}
